import { format } from 'date-fns';

import type { LeaseHistoryChipDto } from '@zg-rentals/rental-manager-env';
import type { SignedUserWebApiDto } from '@zg-rentals/ts-leasing-client';
import { LeaseArtifactType, LeaseStatus } from '@zg-rentals/ts-leasing-client';

import { getFriendlyDate } from '../utils/humanReadable';
import Person from './Person';
import {
  CREATE_STATES,
  DRAFT_STATES,
  READ_ONLY_STATES,
  SIGNING_IN_PROGRESS_STATES,
  STATES_THAT_SHOW_LAST_MESSAGE,
  STATES_THAT_SHOW_WHO_HAS_SIGNED,
} from './Lease/States';
import { LEASING_WEB_API_PREFIX } from '../utils/queries';
import { DocumentBackend } from './Types';
import type { Message, RouterHistory } from './Types';
import { LeaseTypes } from './Types';
import { EXPLAINER_LEASE_PREFIX } from '../utils/staticAsssets';
import { baseUrl } from '../utils/apiConstants';
import { tagColorStatusMap, tagTextStatusMap } from '../constants/stringConstants';
import LeaseExtension from './LeaseExtension';
import { isColandlordPath } from '../constants/routeConstants';

export enum SUMMARY_VIEW_SUBTYPES {
  PREVIEW = 'preview',
}
export enum LEASE_VIEW_TYPES {
  WIZARD = 'wizard',
  SELF_SERVE = 'self-serve',
  SUMMARY = 'list',
  PREVIEW = 'preview',
}

export type TagTone =
  | 'transparent'
  | 'aqua'
  | 'blue'
  | 'critical'
  | 'gray'
  | 'green'
  | 'info'
  | 'orange'
  | 'purple'
  | 'red'
  | 'success'
  | 'teal'
  | 'warning'
  | 'yellow';

class Lease {
  creationDate: Date;
  documentBackend: DocumentBackend;
  encodedId: string;
  endDate: Date;
  id: string;
  isArchived: boolean;
  isSelfUpload: boolean;
  journeyId: string;
  journeyVersionId?: string;
  landlords: Array<Person>;
  latestExtension?: LeaseExtension;
  leaseHistory: Array<LeaseHistoryChipDto>;
  leaseType: LeaseTypes;
  listingAlias?: string;
  messageData?: Message;
  name: string;
  propertyId: string;
  recentActivity: string;
  sharedDate: Date;
  signedUsers?: { leaseId: number; signedUsers: Array<SignedUserWebApiDto> };
  startDate: Date;
  status: LeaseStatus;
  tenants: Array<Person>;
  updateDate: Date;
  userActionExpected: boolean;

  constructor(args) {
    this.creationDate = args.creationDate ? new Date(args.creationDate) : null;
    this.documentBackend = args.documentBackend;
    this.encodedId = args.encodedId;
    this.endDate = args.endDate ? new Date(args.endDate) : null;
    this.id = String(args.id ?? '');
    this.isArchived = args.isArchived || false;
    this.isSelfUpload = args.isSelfUpload || false;
    this.journeyId = args.journeyId;
    this.journeyVersionId = args.journeyVersionId;
    this.landlords = Lease.addPersonsFromApi(args.landlords);
    this.latestExtension = args?.latestExtension ? new LeaseExtension(args.latestExtension) : undefined;
    this.leaseHistory = args?.leaseHistory;
    this.leaseType = args.leaseType;
    this.listingAlias = args?.listingAlias;
    this.messageData = args.messageData;
    this.name = args.name;
    this.propertyId = args.propertyId ? args.propertyId : null;
    this.recentActivity = args.recentActivity;
    this.sharedDate = args.sharedDate ? new Date(args.sharedDate) : null;
    this.signedUsers = args.signedUsers;
    this.startDate = args.startDate ? new Date(args.startDate) : null;
    this.status = args.status;
    this.tenants = Lease.addPersonsFromApi(args.tenants);
    this.updateDate = args.updateDate ? new Date(args.updateDate) : null;
    this.userActionExpected = args.userActionExpected;
  }

  static addPersonsFromApi = (personsFromAPI) => (personsFromAPI || []).map((params) => new Person(params));

  static sortByLastCreatedFirst = (leaseA: Lease, leaseB: Lease): number =>
    leaseB.creationDate.getTime() - leaseA.creationDate.getTime();

  static sortByLastUpdatedFirst = (leaseA: Lease, leaseB: Lease): number =>
    (leaseB.updateDate || leaseB.creationDate).getTime() - (leaseA.updateDate || leaseA.creationDate).getTime();

  static getPDFLink(leaseId, isDraft = false, pathname: string = null) {
    if (pathname == null) {
      pathname = window.location.pathname;
    }

    const isColandlord = isColandlordPath(pathname);

    return isColandlord
      ? `${baseUrl}/${LEASING_WEB_API_PREFIX}/ouid/downloadFromOuid${window.location.search}&draft=${isDraft}`
      : `${baseUrl}/${LEASING_WEB_API_PREFIX}/documents/download?leaseArtifactType=${LeaseArtifactType.LEASE}` +
          `&leaseArtifactId=${leaseId}&draft=${isDraft}`;
  }

  static getDashboardPath(pathname: string = null): string {
    let newPath = '';

    if (pathname != null) {
      newPath = pathname;
    } else if (typeof window !== 'undefined') {
      newPath = window.location.pathname;
    }

    const propertyRegex = /(.*\/property\/[A-Za-z_\-0-9]+)\/?(\?.*)?/;
    const landlordRegexGroups = newPath.match(propertyRegex);
    if (landlordRegexGroups != null) {
      return `${landlordRegexGroups[1]}`;
    }

    const tenantWelcomeRegexGroups = newPath.match(/(.*\/tenant\/welcome)\/?(\?.*)?/);
    if (tenantWelcomeRegexGroups != null) {
      return `/renter-hub/leasing`;
    }

    const tenantRegexGroups = newPath.match(/(.*\/renter-hub\/leasing)\/?(\?.*)?/);
    if (tenantRegexGroups != null) {
      return `${tenantRegexGroups[1]}`;
    }

    const colandlordRegexGroups = newPath.match(/(.*\/colandlord)\/?(\?.*)?/);
    if (colandlordRegexGroups != null) {
      return `${colandlordRegexGroups[1]}`;
    }

    // Test fallback
    const testPath = `/property/somePropertyId/`;
    // eslint-disable-next-line no-console
    console.error(`Could not generate a path, are we in a test environment? Using this fake path instead: ${testPath}`);
    return testPath;
  }

  static getExplainerLeasePath(explainerLeaseName: string): string {
    return `${EXPLAINER_LEASE_PREFIX}/${explainerLeaseName}`;
  }

  static getRootPath(leaseId: number | string, pathname: string = null): string {
    return `${Lease.getDashboardPath(pathname)}/lease/${leaseId}`;
  }

  static getWizardPath = (leaseId: string, specificNode?: string, pathname?: string) =>
    `${Lease.getRootPath(leaseId, pathname)}/${LEASE_VIEW_TYPES.WIZARD}${
      specificNode ? `?nodeId=${specificNode}` : ''
    }`;

  static getSummaryPathBase(leaseId: string, pathname?: string) {
    const rootPath = Lease.getRootPath(leaseId, pathname);
    const isLandlord = rootPath.indexOf('property') >= 0;

    return isLandlord ? `${rootPath}/${LEASE_VIEW_TYPES.SUMMARY}` : rootPath;
  }

  static getSummaryPathFromLocation(leaseId: string, pathname?: string) {
    if ((pathname || window.location.pathname).indexOf(LEASE_VIEW_TYPES.SELF_SERVE) >= 0) {
      return Lease.getSelfServePath(leaseId);
    }

    return Lease.getSummaryPath(leaseId, pathname);
  }

  static getSelfServePath(leaseId: string, nestedView?: SUMMARY_VIEW_SUBTYPES) {
    const rootPath = Lease.getRootPath(leaseId);
    const summaryPath = `${rootPath}/${LEASE_VIEW_TYPES.SELF_SERVE}${nestedView ? `?nestedView=${nestedView}` : ''}`;
    const isColandlord = isColandlordPath(rootPath);

    if (isColandlord) {
      const ouid = Person.getOUIDfromSearch();
      return `${summaryPath}${nestedView ? '&' : '?'}ouid=${ouid}`;
    }

    return summaryPath;
  }

  static getSummaryPath(leaseId: string, pathname?: string) {
    const summaryPath = Lease.getSummaryPathBase(leaseId, pathname);
    const isColandlord = isColandlordPath(summaryPath);

    return isColandlord ? `${summaryPath}${window.location.search}` : summaryPath;
  }

  static getPreviewPath(leaseId: string, pathname?: string) {
    const rootPath = Lease.getRootPath(leaseId, pathname);
    const isColandlord = isColandlordPath(rootPath);
    const previewPath = `${rootPath}/${SUMMARY_VIEW_SUBTYPES.PREVIEW}`;

    return isColandlord ? `${previewPath}${window.location.search}` : previewPath;
  }

  static getManageYourLeasePath(leaseId: string) {
    const rootPath = Lease.getRootPath(leaseId);

    return `${rootPath}/manage`;
  }

  static getReviewPath(leaseId: string) {
    const rootPath = Lease.getRootPath(leaseId);

    return `${rootPath}/review`;
  }

  isEditable = (): boolean => !this.isInSigningState() && !this.isReadOnly();

  isInSigningState = (): boolean => SIGNING_IN_PROGRESS_STATES.has(this.status);

  isDraft = (): boolean => DRAFT_STATES.has(this.status);

  isCreating = (): boolean => CREATE_STATES.has(this.status);

  isReadOnly = (): boolean => READ_ONLY_STATES.has(this.status);

  getLabel(fallback: string = null) {
    if (this.name) {
      return this.name;
    }

    if (this.tenants.length > 0) {
      const firstTenantName = this.tenants[0].getFullName();
      if (firstTenantName) {
        return `${firstTenantName}${firstTenantName.endsWith('s') ? '’' : '’s'} lease`;
      }
    }

    return fallback || this.id;
  }

  getFriendlyStartDate() {
    return this.startDate != null ? getFriendlyDate(this.startDate) : null;
  }

  getFriendlyEndDate() {
    return this.endDate != null ? getFriendlyDate(this.endDate) : null;
  }

  getFriendlyCreationDate() {
    return this.creationDate != null ? getFriendlyDate(this.creationDate) : null;
  }

  getFriendlyUpdatedDate() {
    return this.updateDate != null ? getFriendlyDate(this.updateDate) : null;
  }

  getFriendlySharedDate() {
    return this.sharedDate != null ? getFriendlyDate(this.sharedDate) : null;
  }

  getPDFLink = (isDraft?: boolean): string => Lease.getPDFLink(this.id, isDraft);

  // Builds a relative path for this Lease given the current path
  getRootPath = (pathname: string = null): string =>
    this.isSelfUpload ? this.getSelfServePath() : Lease.getRootPath(this.id, pathname);

  getWizardPath = (specificNode?: string, pathname?: string): string =>
    Lease.getWizardPath(this.id, specificNode, pathname);

  getSelfServePath = (): string => Lease.getSelfServePath(this.id);

  getSelfServePreviewPath = (): string => `${Lease.getSelfServePath(this.id, SUMMARY_VIEW_SUBTYPES.PREVIEW)}`;

  getSummaryPath = (pathname?: string): string =>
    this.isSelfUpload ? Lease.getSelfServePath(this.id) : Lease.getSummaryPath(this.id, pathname);

  getPreviewPath = (pathname?: string): string => Lease.getPreviewPath(this.id, pathname);

  hasEnded = (): boolean => this.endDate != null && this.endDate < new Date();

  hasColandlord = (): boolean => this.landlords.length > 1;

  showWhoHasSigned = (): boolean => STATES_THAT_SHOW_WHO_HAS_SIGNED.has(this.status);

  showLastMessage = (): boolean => STATES_THAT_SHOW_LAST_MESSAGE.has(this.status);

  getHumanReadableStatus(): string {
    if (this.userActionExpected) {
      return 'Awaiting your signature';
    }

    return tagTextStatusMap[this.status] || this.status;
  }

  getBadgeColor(): TagTone {
    if (this.userActionExpected) {
      return 'yellow';
    }

    return (tagColorStatusMap[this.status] as TagTone) || 'blue';
  }

  forceRedirectIfWrongViewType(
    history: RouterHistory,
    viewType: LEASE_VIEW_TYPES,
    trackError: (Error, additionalData) => void,
  ) {
    const unexpectedRedirectError = new Error('Unexpected redirect');
    if (this.isSelfUpload) {
      if (viewType !== LEASE_VIEW_TYPES.SELF_SERVE) {
        trackError(unexpectedRedirectError, { leaseId: this.id, viewType });
        if (history.location?.search.includes('fromApp')) {
          // For mobile, do not touch cause it’s messy
          history.replace(`${this.getSelfServePath()}${history.location.search}`);
        } else {
          history.replace(this.getSelfServePath());
        }
      }
    } else if (viewType === LEASE_VIEW_TYPES.SELF_SERVE) {
      trackError(unexpectedRedirectError, { leaseId: this.id, viewType });
      history.replace(this.getSummaryPath());
    }
  }

  getDownloadFileName() {
    const parts = ['Lease'];

    if (this.tenants[0]?.firstName) {
      parts.push(this.tenants[0].firstName);
    }
    if (this.tenants[0]?.lastName) {
      parts.push(this.tenants[0].lastName);
    }
    parts.push(format(this.creationDate, 'yyyyMMdd'));
    return parts.join('-');
  }

  isEligibleForLeaseRenewal() {
    // Only Zillow leases are eligible for lease renewal
    if (this.leaseType == LeaseTypes.SELF_UPLOAD || this.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD) {
      return false;
    }
    // Dotloop leases are not eligible for lease renewal
    if (this.documentBackend === DocumentBackend.dotloop) {
      return false;
    }
    // Only leases with end dates are eligible for lease renewal
    if (!this.endDate) {
      return false;
    }
    // Only leases without extensions
    if (this.latestExtension) {
      return false;
    }
    // When we start supporting renewal on renewal
    // if (this?.latestExtension) {
    //   if (
    //     this.latestExtension.endDate &&
    //     (this.latestExtension.status === LeaseStatus.EXPIRED || this.latestExtension.status === LeaseStatus.ACTIVE)
    //   ) {
    //     return true;
    //   } else {
    //     return false;
    //   }
    // }
    // Only if lease is expired or active
    if (this.status === LeaseStatus.EXPIRED || this.status === LeaseStatus.ACTIVE) {
      return true;
    }
    return false;
  }

  // If there is a ratified lease extension, take the end date of that, otherwise take the end date of the lease.
  getLeasePackageFriendlyEndDate() {
    const latestExtension = this?.latestExtension ?? null;

    return latestExtension?.isReadOnly()
      ? latestExtension?.endDate
        ? latestExtension?.getFriendlyEndDate?.()
        : null
      : this?.endDate
        ? this?.getFriendlyEndDate()
        : null;
  }
}

export default Lease;
